/**
 * Environment Variable Configuration and Validation
 *
 * SECURITY NOTICE:
 * - Only variables prefixed with REACT_APP_ are exposed to the client
 * - NEVER add secrets, API keys, or credentials here
 * - All values here are PUBLIC and visible in the browser
 */

/**
 * Environment variable schema
 */
interface EnvConfig {
    // App configuration
    isDevelopment: boolean;
    isProduction: boolean;
    mode: string;
}

/**
 * Validate required environment variables
 * Throws an error if required variables are missing
 */
function validateEnv(): void {
    const required: string[] = [];

    const missing = required.filter(key => !process.env[key]);

    if (missing.length > 0) {
        throw new Error(
            `Missing required environment variables:\n${missing.join('\n')}\n\n` +
                'Please check your .env file and ensure all required variables are set.\n' +
                'See .env.example for reference.'
        );
    }
}

/**
 * Get environment configuration
 * Validates and returns typed environment variables
 *
 * @throws {Error} If required environment variables are missing
 */
export function getEnvConfig(): EnvConfig {
    // Validate environment variables in development
    if (import.meta.env.DEV) {
        validateEnv();
    }

    return {
        isDevelopment: import.meta.env.DEV,
        isProduction: import.meta.env.PROD,
        mode: import.meta.env.MODE,
    };
}

/**
 * Environment configuration singleton
 * Use this instead of directly accessing process.env
 */
export const env = getEnvConfig();

/**
 * Helper to check if running in development mode
 */
export const isDev = env.isDevelopment;

/**
 * Helper to check if running in production mode
 */
export const isProd = env.isProduction;

/**
 * SECURITY WARNINGS:
 *
 * 1. DO NOT add secret keys, tokens, or credentials to this file
 * 2. All REACT_APP_* variables are PUBLIC and visible in browser
 * 3. Use backend API for sensitive operations
 * 4. Never expose:
 *    - Database credentials
 *    - API secrets
 *    - Private keys
 *    - Access tokens
 *    - Any credential or secret
 *
 * If you need to use secrets:
 * - Store them on the backend
 * - Use secure authentication (Keycloak)
 * - Never send secrets to the client
 */
